import { type ComputedRef, computed } from 'vue'
import { extractUserAvatar, extractUserId, extractUserName, normalizeAvatarUrl, normalizeReactionIconUrl } from '../utils/userDataExtractor'
import { getCurrentMatrixUserId } from '../services/matrixChatClient'
import { findViewAvatarByMatrixUserId } from '../stores/chatStoreSync'
import { isMatrixMediaHttpUrl } from '../services/matrixMediaUrl'
import { getReactionEmoji, stripEmojiKeyPrefix } from '../utils/reactionUtils'
import type { Reaction } from '../types'

export interface GroupedReaction {
  reactionTypeId: string | number
  emoji?: string
  icon?: string
  users: ReactionUser[]
  count: number
  isMyReaction: boolean
  name?: string
  isImage?: boolean
  isThumb?: boolean
  tooltip?: string
}

export interface ReactionUser {
  id: string
  name: string
  avatar: string | null
}

export const useReactionGrouping = (
  currentUserId: ComputedRef<string | number | undefined>,
  reactionTypes: ComputedRef<any[]>,
  chatMembers?: ComputedRef<any[]>,
  allMessages?: ComputedRef<any[]>,
  profileUser?: ComputedRef<{ fullName?: string; avatar?: string | null } | null | undefined>,
) => {
  const isCurrentUser = (userId: string): boolean => {
    if (currentUserId.value && String(userId) === String(currentUserId.value)) {
      return true
    }

    const matrixUserId = getCurrentMatrixUserId()
    return matrixUserId ? String(userId) === matrixUserId : false
  }

  const extractReactionTypeId = (reaction: any): string => {
    const numericId = Number(reaction.reaction_type_id ?? reaction.reaction_type)
    if (Number.isFinite(numericId) && numericId > 0 && numericId < 10000) {
      return String(numericId)
    }

    const matrixKey = stripEmojiKeyPrefix(reaction.matrix_emoji_key)
    if (matrixKey) {
      return `emoji:${matrixKey}`
    }

    const raw = String(
      reaction.reaction_type_id
      || reaction.reaction_type
      || reaction.type_id
      || 'unknown',
    )
    if (raw === 'unknown') {
      return raw
    }

    const stripped = stripEmojiKeyPrefix(raw)
    return stripped ? `emoji:${stripped}` : 'unknown'
  }

  const buildReactionTypeMeta = (reaction: Reaction, reactionTypeId: string) => {
    const emojiFromMatrix = stripEmojiKeyPrefix(
      reaction.matrix_emoji_key
      || (reactionTypeId.startsWith('emoji:') ? reactionTypeId.slice(6) : ''),
    )

    const reactionType = emojiFromMatrix
      ? reactionTypes.value.find(rt => {
        const rawIcon = String(rt.icon || '')
        const icon = normalizeReactionIconUrl(rawIcon) || rawIcon
        return icon === emojiFromMatrix || rt.emoji === emojiFromMatrix
      })
      : reactionTypes.value.find(rt => rt.id === Number(reactionTypeId))

    const rawIcon = reactionType?.icon || emojiFromMatrix || ''
    const icon = normalizeReactionIconUrl(rawIcon) || rawIcon

    const isImage
      = !emojiFromMatrix
      && (icon.includes('/') || icon.match(/\.(png|jpg|jpeg|gif|svg|webp)$/i))

    let emoji = emojiFromMatrix || ''
    if (!emoji && !isImage && reactionType) {
      emoji = getReactionEmoji(reactionType) || reactionType.emoji || ''
    }
    emoji = stripEmojiKeyPrefix(emoji)

    return {
      reactionTypeId: reactionType?.id ?? reactionTypeId,
      emoji,
      icon,
      name: reactionType?.name || emoji || 'Reaction',
      isImage,
      isThumb: emoji === '👍' || icon?.toLowerCase().includes('thumb'),
    }
  }

  const buildReactionUser = (reaction: Reaction, userId: string, message?: any): ReactionUser => {
    const messages = allMessages?.value || []
    // Реакции хранят MXID; в списке/личке аватар — S3 сотрудника. Matrix mxc в <img> без auth не грузится.
    const contactAvatar = userId.startsWith('@')
      ? findViewAvatarByMatrixUserId(userId)
      : null

    let userAvatar = contactAvatar
    if (!userAvatar && message) {
      const messageSenderId = String(message.senderId || message.created_by?.id || '')
      if (messageSenderId && String(userId) === messageSenderId) {
        userAvatar = normalizeAvatarUrl(message.avatar || message.created_by?.avatar)
      }
    }

    if (!userAvatar && messages.length > 0) {
      const userMessage = messages.find((msg: any) => {
        const msgSenderId = String(msg.senderId || msg.created_by?.id || '')

        return msgSenderId && String(msgSenderId) === String(userId)
      })

      if (userMessage) {
        userAvatar = normalizeAvatarUrl(userMessage.avatar || userMessage.created_by?.avatar)
      }
    }

    if (!userAvatar) {
      userAvatar = extractUserAvatar(reaction, chatMembers?.value)
    }

    if (userAvatar && isMatrixMediaHttpUrl(userAvatar) && contactAvatar) {
      userAvatar = contactAvatar
    }

    const userName = isCurrentUser(userId) && profileUser?.value?.fullName
      ? profileUser.value.fullName
      : extractUserName(reaction)

    if (isCurrentUser(userId) && profileUser?.value?.avatar) {
      userAvatar = normalizeAvatarUrl(profileUser.value.avatar) || userAvatar
    }

    return {
      id: userId,
      name: userName,
      avatar: userAvatar,
    }
  }

  const groupReactions = (reactions: Reaction[], message?: any): GroupedReaction[] => {
    if (!reactions || !Array.isArray(reactions) || !reactions.length) {
      return []
    }

    const latestByUser = new Map<string, Reaction>()

    for (const reaction of reactions) {
      const userId = String(extractUserId(reaction))
      if (!userId) {
        continue
      }

      latestByUser.set(userId, reaction)
    }

    const groups = new Map<string, GroupedReaction>()

    for (const [userId, reaction] of latestByUser.entries()) {
      const reactionTypeId = extractReactionTypeId(reaction)
      if (reactionTypeId === 'unknown') {
        continue
      }

      const meta = buildReactionTypeMeta(reaction, reactionTypeId)
      const groupKey = meta.isImage
        ? `img:${meta.icon || meta.name}`
        : `emoji:${stripEmojiKeyPrefix(meta.emoji || String(meta.reactionTypeId))}`
      const user = buildReactionUser(reaction, userId, message)
      const existing = groups.get(groupKey)

      if (existing) {
        if (!existing.users.some(item => item.id === user.id)) {
          existing.users.push(user)
          existing.count = existing.users.length
        }
        existing.isMyReaction = existing.isMyReaction || isCurrentUser(userId)
        continue
      }

      const label = meta.isImage ? (meta.name || 'Reaction') : (meta.emoji || meta.name || 'Reaction')
      groups.set(groupKey, {
        ...meta,
        users: [user],
        count: 1,
        isMyReaction: isCurrentUser(userId),
        tooltip: label,
      })
    }

    return Array.from(groups.values()).map(group => {
      const label = group.isImage ? (group.name || 'Reaction') : (group.emoji || group.name || 'Reaction')
      const names = group.users.map(u => u.name).filter(Boolean).join(', ')
      return {
        ...group,
        tooltip: names ? `${label} · ${names}` : label,
      }
    })
  }

  const extractReactionsArray = (input: Reaction[] | any): Reaction[] => {
    if (Array.isArray(input)) {
      return input
    }

    if (input && typeof input === 'object') {
      return input.reactions || input.message_reactions || []
    }

    return []
  }

  const getMyReaction = (reactions: Reaction[] | any): Reaction | null => {
    if (!reactions) {
      return null
    }

    if (!currentUserId.value && !getCurrentMatrixUserId()) {
      return null
    }

    const reactionsArray = extractReactionsArray(reactions)
    if (!Array.isArray(reactionsArray) || reactionsArray.length === 0) {
      return null
    }

    return reactionsArray.filter(r => {
      const userId = extractUserId(r)

      return isCurrentUser(String(userId))
    }).pop() || null
  }

  const getMyReactionTypeId = (reactionsOrMessage: Reaction[] | any): string | null => {
    const myReaction = getMyReaction(reactionsOrMessage)
    if (!myReaction) {
      return null
    }

    if (myReaction.reaction_type_id != null) {
      return String(myReaction.reaction_type_id)
    }

    if (myReaction.reaction_type != null && typeof myReaction.reaction_type === 'number') {
      return String(myReaction.reaction_type)
    }

    const emojiKey = stripEmojiKeyPrefix((myReaction as any).matrix_emoji_key)
    if (emojiKey) {
      const match = reactionTypes.value.find(rt => {
        const rawIcon = String(rt.icon || '')
        const icon = normalizeReactionIconUrl(rawIcon) || rawIcon
        return icon === emojiKey || rt.emoji === emojiKey
      })

      if (match) {
        return String(match.id)
      }

      return `emoji:${emojiKey}`
    }

    return extractReactionTypeId(myReaction)
  }

  return {
    groupReactions,
    getMyReaction,
    getMyReactionTypeId,
  }
}
